import { createContext, useContext } from 'react';

export type ToastTone = 'info' | 'success' | 'error';

export interface ToastOptions {
  message: string;
  tone?: ToastTone | undefined;
  title?: string | undefined;
  /**
   * How long before it dismisses itself. An error defaults to staying until it
   * is closed: a driver who taps "collected" and gets a 409 while walking back
   * to the van must not have the reason vanish before they look.
   */
  durationMs?: number | undefined;
}

export interface Toast extends ToastOptions {
  id: string;
  tone: ToastTone;
}

export interface ToastApi {
  /** Returns the id, so a long-running action can dismiss its own toast. */
  show: (options: ToastOptions) => string;
  success: (message: string, title?: string) => string;
  error: (message: string, title?: string) => string;
  info: (message: string, title?: string) => string;
  dismiss: (id: string) => void;
}

export const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const value = useContext(ToastContext);
  if (value === null) {
    throw new Error('useToast must be used inside <ToastProvider>');
  }
  return value;
}
